import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { createTheme, alpha } from '@mui/material/styles';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

// Theme mode options
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// App-wide interface size (shared by every page — the whole UI scales together).
// Implemented as root font-size scaling: MUI typography is rem-based and theme
// spacing is rem (below), so text and padding scale everywhere while popup
// positioning stays in real pixels. (CSS zoom was tried first and broke MUI
// menu/popover anchoring — do not reintroduce it.)
export const UI_SIZES = {
  compact: { label: 'Compact', factor: 0.9 },
  medium: { label: 'Medium', factor: 1 },
  large: { label: 'Large', factor: 1.15 },
};
const UI_SIZE_ORDER = ['compact', 'medium', 'large'];

// Custom color palettes
const customColors = {
  // Vibrant gradients
  gradients: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    warning: 'linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)',
    error: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    dark: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    glass: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    mesh: 'radial-gradient(at 40% 20%, hsla(280,100%,74%,1) 0px, transparent 50%), radial-gradient(at 80% 0%, hsla(189,100%,56%,1) 0px, transparent 50%), radial-gradient(at 0% 50%, hsla(355,100%,93%,1) 0px, transparent 50%)',
  },
  // Neon colors for accents
  neon: {
    blue: '#00d9ff',
    purple: '#bd00ff',
    pink: '#ff006e',
    green: '#00ff88',
    yellow: '#ffea00',
  },
  // Glassmorphism colors
  glass: {
    white: 'rgba(255, 255, 255, 0.1)',
    black: 'rgba(0, 0, 0, 0.3)',
    border: 'rgba(255, 255, 255, 0.18)',
  }
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#7c8ff0',
      dark: '#5569e4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f093fb',
      light: '#f4a8fc',
      dark: '#ed7efa',
      contrastText: '#ffffff',
    },
    success: {
      main: '#11998e',
      light: '#38ef7d',
      dark: '#0d7a6a',
    },
    error: {
      main: '#eb3349',
      light: '#f45c43',
      dark: '#d91e33',
    },
    warning: {
      main: '#F2994A',
      light: '#F2C94C',
      dark: '#e88936',
    },
    background: {
      default: '#f7f9fc',
      paper: '#ffffff',
      gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    },
    text: {
      primary: '#1a1d29',
      secondary: '#6b7588',
      disabled: '#a8b0c0',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    custom: customColors,
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { 
      fontWeight: 800, 
      fontSize: '3.5rem', 
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: { 
      fontWeight: 700, 
      fontSize: '2.75rem', 
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: { 
      fontWeight: 600, 
      fontSize: '2.25rem', 
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    h4: { 
      fontWeight: 600, 
      fontSize: '1.875rem', 
      lineHeight: 1.35,
    },
    h5: { 
      fontWeight: 600, 
      fontSize: '1.5rem', 
      lineHeight: 1.4,
    },
    h6: { 
      fontWeight: 600, 
      fontSize: '1.25rem', 
      lineHeight: 1.45,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: { 
      textTransform: 'none', 
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  // rem spacing: identical to the 8px default at Medium, scales with the
  // interface-size root font (see UI_SIZES above)
  spacing: (n) => `${n * 0.5}rem`,
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.10)',
    '0px 16px 24px rgba(0,0,0,0.12)',
    '0px 24px 32px rgba(0,0,0,0.14)',
    '0px 32px 48px rgba(0,0,0,0.16)',
    ...Array(18).fill('0px 40px 64px rgba(0,0,0,0.18)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontSize: '0.925rem',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        },
        contained: {
          background: customColors.gradients.primary,
          '&:hover': {
            background: customColors.gradients.primary,
            filter: 'brightness(1.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 20,
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 16,
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#7c8ff0',
      light: '#9aa6f5',
      dark: '#667eea',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f093fb',
      light: '#f4a8fc',
      dark: '#ed7efa',
      contrastText: '#ffffff',
    },
    success: {
      main: '#38ef7d',
      light: '#5ff392',
      dark: '#11998e',
    },
    error: {
      main: '#f45c43',
      light: '#f67760',
      dark: '#eb3349',
    },
    warning: {
      main: '#F2C94C',
      light: '#f4d166',
      dark: '#F2994A',
    },
    background: {
      default: '#0a0b0f',
      paper: '#13141a',
      gradient: 'linear-gradient(135deg, #0a0b0f 0%, #1a1b23 100%)',
    },
    text: {
      primary: '#f7f8fa',
      secondary: '#a8b2c8',
      disabled: '#5a6478',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    custom: customColors,
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { 
      fontWeight: 800, 
      fontSize: '3.5rem', 
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: { 
      fontWeight: 700, 
      fontSize: '2.75rem', 
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: { 
      fontWeight: 600, 
      fontSize: '2.25rem', 
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    h4: { 
      fontWeight: 600, 
      fontSize: '1.875rem', 
      lineHeight: 1.35,
    },
    h5: { 
      fontWeight: 600, 
      fontSize: '1.5rem', 
      lineHeight: 1.4,
    },
    h6: { 
      fontWeight: 600, 
      fontSize: '1.25rem', 
      lineHeight: 1.45,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: { 
      textTransform: 'none', 
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  // rem spacing: identical to the 8px default at Medium, scales with the
  // interface-size root font (see UI_SIZES above)
  spacing: (n) => `${n * 0.5}rem`,
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.2)',
    '0px 4px 8px rgba(0,0,0,0.25)',
    '0px 8px 16px rgba(0,0,0,0.30)',
    '0px 16px 24px rgba(0,0,0,0.35)',
    '0px 24px 32px rgba(0,0,0,0.40)',
    '0px 32px 48px rgba(0,0,0,0.45)',
    ...Array(18).fill('0px 40px 64px rgba(0,0,0,0.50)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontSize: '0.925rem',
          fontWeight: 600,
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          },
        },
        contained: {
          background: customColors.gradients.primary,
          '&:hover': {
            background: customColors.gradients.primary,
            filter: 'brightness(1.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha('#13141a', 0.8),
          backdropFilter: 'blur(10px)',
          borderRadius: 20,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha('#13141a', 0.9),
          backdropFilter: 'blur(10px)',
          borderRadius: 16,
        },
      },
    },
  },
});

// Hook to detect system color scheme preference
function useSystemTheme() {
  const [systemTheme, setSystemTheme] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return systemTheme;
}

export function ThemeProvider({ children }) {
  // User's preference: 'light', 'dark', or 'system'
  const [themePreference, setThemePreference] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    // Migrate old values - if it's 'light' or 'dark', keep it; otherwise default to 'system'
    if (savedMode === 'light' || savedMode === 'dark' || savedMode === 'system') {
      return savedMode;
    }
    return 'system';
  });

  // Interface size preference (compact / medium / large)
  const [uiSize, setUiSizeState] = useState(() => {
    const saved = localStorage.getItem('uiSize');
    return saved && UI_SIZES[saved] ? saved : 'medium';
  });

  useEffect(() => {
    localStorage.setItem('uiSize', uiSize);
    document.documentElement.style.fontSize = `${16 * UI_SIZES[uiSize].factor}px`;
  }, [uiSize]);

  const setUiSize = useCallback((size) => {
    if (UI_SIZES[size]) setUiSizeState(size);
  }, []);

  const cycleUiSize = useCallback(() => {
    setUiSizeState(prev => UI_SIZE_ORDER[(UI_SIZE_ORDER.indexOf(prev) + 1) % UI_SIZE_ORDER.length]);
  }, []);

  // Get the actual system theme
  const systemTheme = useSystemTheme();

  // Resolve the effective theme (what's actually displayed)
  const effectiveMode = useMemo(() => {
    if (themePreference === 'system') {
      return systemTheme;
    }
    return themePreference;
  }, [themePreference, systemTheme]);

  const theme = effectiveMode === 'light' ? lightTheme : darkTheme;

  useEffect(() => {
    localStorage.setItem('themeMode', themePreference);

    // Update body background
    document.body.style.background = theme.palette.background.gradient;

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', effectiveMode === 'dark' ? '#0a0b0f' : '#f7f9fc');
    }

    // Add theme transition class
    document.body.classList.add('theme-transition');
    setTimeout(() => {
      document.body.classList.remove('theme-transition');
    }, 300);
  }, [themePreference, effectiveMode, theme]);

  // Toggle between light and dark (skipping system for quick toggle)
  const toggleTheme = useCallback(() => {
    setThemePreference(prev => {
      if (prev === 'system') {
        // If system, toggle to the opposite of current effective mode
        return effectiveMode === 'dark' ? 'light' : 'dark';
      }
      return prev === 'light' ? 'dark' : 'light';
    });
  }, [effectiveMode]);

  // Set a specific theme mode
  const setThemeMode = useCallback((mode) => {
    if (mode === 'light' || mode === 'dark' || mode === 'system') {
      setThemePreference(mode);
    }
  }, []);

  // Cycle through all three modes: light -> dark -> system -> light
  const cycleTheme = useCallback(() => {
    setThemePreference(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, []);

  const value = useMemo(() => ({
    mode: effectiveMode,           // The actual theme being displayed ('light' or 'dark')
    themePreference,               // User's preference ('light', 'dark', or 'system')
    theme,
    toggleTheme,
    setThemeMode,
    cycleTheme,
    isSystemTheme: themePreference === 'system',
    uiSize,                        // 'compact' | 'medium' | 'large'
    setUiSize,
    cycleUiSize,
    sizeFactor: UI_SIZES[uiSize].factor, // for future pages that want to scale spacing directly
  }), [effectiveMode, themePreference, theme, toggleTheme, setThemeMode, cycleTheme, uiSize, setUiSize, cycleUiSize]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}